import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormGroup } from '../../../../../../../components/form/FormGroup';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import { useCertificateByIdQuery } from '../../../../../__generated__/graphql';
import ServerError from '../../../../../../../components/ServerError';
import Skeleton from 'react-loading-skeleton';
import FormattedDate from '../../../../../../../components/date/FormattedDate';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';

type Props = {
  certificateId: string;
};

const EMPTY_VALUE = '---';
const i18Path = 'certificates';

export const CertificateDetails: React.FC<Props> = ({ certificateId }) => {
  const t = useNamespacedTranslation(i18Path);

  const { data, loading, error } = useCertificateByIdQuery({
    variables: { id: certificateId },
  });

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ServerError />;
  }

  const cert = data?.employeeCertificate;

  if (!cert) {
    return <ServerError />;
  }

  const issueDate = cert.flair__Issue_Date__c
    ? new Date(cert.flair__Issue_Date__c)
    : null;
  const expirationDate = cert.flair__Expiration_Date__c
    ? new Date(cert.flair__Expiration_Date__c)
    : null;
  const plannedDate = cert.flair__Planned_Date__c
    ? new Date(cert.flair__Planned_Date__c)
    : null;

  return (
    <div className="mt-4">
      <h2 className="fw-bold mb-3">{t('title')}</h2>
      <Row>
        <Col sm={6}>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('table.header.issuedBy')}
            </Form.Label>
            <p>{cert.flair__Issued_By__c || EMPTY_VALUE}</p>
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('table.header.status')}
            </Form.Label>
            <p>{cert.flair__Status__c || EMPTY_VALUE}</p>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col sm={6}>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('table.header.issueDate')}
            </Form.Label>
            <p>
              {issueDate ? (
                <FormattedDate day={issueDate} format="short" />
              ) : plannedDate ? (
                <FormattedDate day={plannedDate} format="short" />
              ) : (
                EMPTY_VALUE
              )}
            </p>
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('table.header.expirationDate')}
            </Form.Label>
            <p>
              {expirationDate ? (
                <FormattedDate day={expirationDate} format="short" />
              ) : issueDate ? (
                t('table.noExpirationDate')
              ) : (
                EMPTY_VALUE
              )}
            </p>
          </FormGroup>
        </Col>
      </Row>

      {cert.flair__Certificate_Link__c && (
        <Row>
          <Col>
            <FormGroup>
              <Form.Label className="fw-bold">
                {t('certificateLink')}
              </Form.Label>
              <div>
                <a
                  target="_blank"
                  rel="noreferrer noopener"
                  href={cert.flair__Certificate_Link__c}>
                  {cert.flair__Certificate_Link__c}
                  <span className="ms-2">
                    <FlairIcon size="xs" icon="external-link-outline" />
                  </span>
                </a>
              </div>
            </FormGroup>
          </Col>
        </Row>
      )}
    </div>
  );
};

const Loading: React.FC = () => {
  return (
    <div>
      <Skeleton height={80} />
      <Skeleton height={80} />
      <Skeleton height={80} />
    </div>
  );
};
